import React from 'react';

interface RuleSection {
  id: string;
  title: string;
  icon: string;
  color: string;
  rules: { title: string; description: string; severity?: 'info' | 'warning' | 'danger' }[];
}

const RULES_SECTIONS: RuleSection[] = [
  {
    id: 'general',
    title: 'Общие положения',
    icon: '📋',
    color: 'from-blue-500 to-cyan-500',
    rules: [
      {
        title: 'Регистрация',
        description: 'Каждый пользователь может иметь только один аккаунт. Запрещено создавать фейковые профили для обхода ограничений.',
        severity: 'warning'
      },
      {
        title: 'Возрастные ограничения',
        description: 'Сервис предназначен для пользователей старше 13 лет. Администрация оставляет за собой право блокировать аккаунты несовершеннолетних без предупреждения.',
        severity: 'info'
      },
      {
        title: 'Ответственность',
        description: 'Пользователь несет полную ответственность за все действия, совершенные с его аккаунта, включая публикацию контента и общение с другими участниками.',
        severity: 'info'
      }
    ]
  },
  {
    id: 'behavior',
    title: 'Поведение в сообществе',
    icon: '🤝',
    color: 'from-emerald-500 to-green-500',
    rules: [
      {
        title: 'Уважение к другим',
        description: 'Запрещены оскорбления, угрозы, дискриминация по любому признаку (раса, пол, религия, ориентация). Будьте вежливы и уважайте других участников.',
        severity: 'danger'
      },
      {
        title: 'Спам и флуд',
        description: 'Запрещена массовая рассылка одинаковых сообщений, публикация бессмысленного контента, засорение ленты и чатов.',
        severity: 'warning'
      },
      {
        title: 'Троллинг и провокации',
        description: 'Намеренное провоцирование конфликтов, распространение слухов и клеветы строго запрещено и карается блокировкой.',
        severity: 'danger'
      },
      {
        title: 'Реклама',
        description: 'Публикация рекламы без согласования с администрацией запрещена. Это касается постов, сообщений и комментариев.',
        severity: 'warning'
      }
    ]
  },
  {
    id: 'content',
    title: 'Контент и публикации',
    icon: '',
    color: 'from-purple-500 to-pink-500',
    rules: [
      {
        title: 'Запрещенный контент',
        description: 'Строго запрещено публиковать: порнографию, материалы с насилием, экстремистскую символику, призывы к суициду, личные данные других людей без их согласия.',
        severity: 'danger'
      },
      {
        title: 'Авторские права',
        description: 'Публикуйте только свой контент или материалы с открытой лицензией. При использовании чужих работ указывайте авторство.',
        severity: 'warning'
      },
      {
        title: 'Фейковые новости',
        description: 'Распространение заведомо ложной информации, манипуляции фактами и дезинформация запрещены.',
        severity: 'danger'
      },
      {
        title: 'Размер файлов',
        description: 'Максимальный размер загружаемого файла — 10 МБ. Поддерживаемые форматы: JPG, PNG, GIF, MP4, PDF.',
        severity: 'info'
      }
    ]
  },
  {
    id: 'messages',
    title: 'Сообщения и стена',
    icon: '💬',
    color: 'from-yellow-500 to-orange-500',
    rules: [
      {
        title: 'Личные сообщения',
        description: 'Запрещено отправлять нежелательные сообщения (спам), угрозы и оскорбления в личных переписках.',
        severity: 'danger'
      },
      {
        title: 'Стена профиля',
        description: 'Публикуйте на стене только уместный контент. Запрещено использовать стену для рекламы и спама.',
        severity: 'warning'
      },
      {
        title: 'Похвала и выговоры',
        description: 'Используйте систему похвалы и выговоров ответственно. Злоупотребление может привести к ограничению функций.',
        severity: 'info'
      }
    ]
  },
  {
    id: 'clan',
    title: 'Клан и битвы',
    icon: '🐺',
    color: 'from-red-500 to-rose-500',
    rules: [
      {
        title: 'Вступление в клан',
        description: 'Каждый пользователь может состоять только в одном клане одновременно. Выход из клана возможен в любое время.',
        severity: 'info'
      },
      {
        title: 'Поведение в клане',
        description: 'Уважайте лидера и участников клана. Конфликты решайте мирно или через администрацию.',
        severity: 'warning'
      },
      {
        title: 'Битвы',
        description: 'Участвуйте в битвах честно. Запрещено использование багов, читов и сговоров для получения преимущества.',
        severity: 'danger'
      }
    ]
  },
  {
    id: 'shop',
    title: 'Магазин и валюта',
    icon: '🛒',
    color: 'from-indigo-500 to-blue-500',
    rules: [
      {
        title: 'Валюта (монеты)',
        description: 'Монеты начисляются за активность: публикации, лайки, участие в битвах. Продажа монет за реальные деньги между пользователями запрещена.',
        severity: 'warning'
      },
      {
        title: 'Покупки',
        description: 'Все покупки в магазине окончательны и возврату не подлежат. Внимательно проверяйте товар перед покупкой.',
        severity: 'info'
      },
      {
        title: 'Стикеры',
        description: 'Стикеры предназначены только для использования в чатах. Перепродажа стикерпаков запрещена.',
        severity: 'warning'
      }
    ]
  },
  {
    id: 'punishments',
    title: 'Нарушения и наказания',
    icon: '⚖️',
    color: 'from-gray-500 to-slate-500',
    rules: [
      {
        title: 'Система предупреждений',
        description: 'Первое нарушение — предупреждение. Второе — временная блокировка (24 часа). Третье — блокировка на 7 дней. Четвертое — перманентный бан.',
        severity: 'warning'
      },
      {
        title: 'Немедленный бан',
        description: 'За серьезные нарушения (угрозы, распространение запрещенного контента, взлом аккаунтов) аккаунт блокируется без предупреждений.',
        severity: 'danger'
      },
      {
        title: 'Обжалование',
        description: 'Если вы считаете блокировку несправедливой, напишите в поддержку через раздел "Связь". Решение администрации является окончательным.',
        severity: 'info'
      }
    ]
  }
];

const RulesPage: React.FC = () => {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getSeverityStyles = (severity?: string) => {
    switch (severity) {
      case 'danger':
        return 'border-l-red-500 bg-red-500/5';
      case 'warning':
        return 'border-l-yellow-500 bg-yellow-500/5';
      case 'info':
      default:
        return 'border-l-blue-500 bg-blue-500/5';
    }
  };

  const getSeverityBadge = (severity?: string) => {
    switch (severity) {
      case 'danger':
        return { text: 'Строго запрещено', color: 'bg-red-500/20 text-red-400 border-red-500/30' };
      case 'warning':
        return { text: 'Внимание', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' };
      case 'info':
      default:
        return { text: 'Информация', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1115] text-white pb-20">
      <main className="max-w-6xl mx-auto p-6">
        {/* Шапка */}
        <div className="text-center mb-12 pt-8 pb-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-emerald-400 px-4 leading-tight">
            🦈Правила Sharkly🦈
          </h1>
          <p className="text-gray-400 text-sm md:text-base lg:text-lg max-w-2xl mx-auto px-4 leading-relaxed">
            Добро пожаловать в наше сообщество! Пожалуйста, внимательно ознакомьтесь с правилами,
            чтобы сделать пребывание на платформе комфортным для всех участников.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full mx-4">
            <span className="text-yellow-400">⚠️</span>
            <span className="text-xs md:text-sm text-yellow-400 font-medium">
              Нарушение правил может привести к блокировке аккаунта
            </span>
          </div>
        </div>

        {/* Навигация по разделам */}
        <div className="bg-[#1a1d24] rounded-3xl border border-gray-800 p-6 mb-12">
          <h2 className="text-xl font-bold mb-4 text-gray-300"> Содержание</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {RULES_SECTIONS.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className="flex items-center gap-3 px-4 py-3 bg-[#0f1115] hover:bg-gray-800 rounded-xl border border-gray-700 hover:border-gray-600 transition text-left group"
              >
                <span className="text-2xl group-hover:scale-110 transition">{section.icon}</span>
                <span className="font-medium text-gray-300 group-hover:text-white">{section.title}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Разделы правил */}
        <div className="space-y-12">
          {RULES_SECTIONS.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-6">
              {/* Заголовок раздела */}
              <div className="flex items-center gap-4 mb-6">
                <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${section.color} flex items-center justify-center text-3xl shadow-lg`}>
                  {section.icon}
                </div>
                <h2 className="text-3xl font-black text-white">{section.title}</h2>
              </div>

              {/* Правила раздела */}
              <div className="space-y-4">
                {section.rules.map((rule, index) => {
                  const badge = getSeverityBadge(rule.severity);
                  return (
                    <div
                      key={index}
                      className={`bg-[#1a1d24] rounded-2xl border-l-4 ${getSeverityStyles(rule.severity)} border border-gray-800 p-6 hover:border-gray-700 transition`}
                    >
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                          <span className="text-gray-500 font-mono text-sm">#{index + 1}</span>
                          {rule.title}
                        </h3>
                        <span className={`text-xs px-3 py-1 rounded-full border font-bold shrink-0 ${badge.color}`}>
                          {badge.text}
                        </span>
                      </div>
                      <p className="text-gray-400 leading-relaxed pl-8">
                        {rule.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Футер */}
        <div className="mt-16 bg-gradient-to-r from-blue-600/20 to-emerald-600/20 rounded-3xl border border-blue-500/30 p-8 text-center">
          <h3 className="text-2xl font-bold mb-3">🤝 Спасибо за соблюдение правил!</h3>
          <p className="text-gray-300 mb-6">
            Вместе мы делаем Sharkly лучшим местом для общения и творчества.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span>📅</span>
              <span>Последнее обновление: 17 июля 2026</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-400">
              <span>📧</span>
              <span>Вопросы? Пишите в раздел "Связь"</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RulesPage;